import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useInvoiceStore } from '../stores/invoiceStore';
import InlineEdit from './InlineEdit';

interface InvoiceNumberEditProps {
  isEditing: boolean;
  onEditClick: () => void;
  onSave: () => void;
}

const DEFAULT_FORMAT = {
  prefix: 'INV',
  includeYear: true,
  yearFormat: 'full' as const,
  separator: '-',
  numberPadding: 4
};

const InvoiceNumberEdit: React.FC<InvoiceNumberEditProps> = ({
  isEditing,
  onEditClick,
  onSave,
}) => {
  const { t } = useTranslation();
  const { settings, updateSettings, getNextInvoiceNumber } = useInvoiceStore();
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const format = settings.invoiceNumberFormat;

  // Use current invoice number if it exists, otherwise show next number
  const displayNumber = currentInvoice.invoiceNumber || getNextInvoiceNumber();

  const handleFormatChange = (changes: Partial<typeof format>) => {
    updateSettings({
      invoiceNumberFormat: {
        ...format,
        ...changes
      }
    });
  };

  const handleReset = () => {
    updateSettings({ invoiceNumberFormat: DEFAULT_FORMAT });
  };

  return (
    <InlineEdit
      isEditing={isEditing}
      onEditClick={onEditClick}
      onSave={onSave}
      showDefaultSaveButton={false}
      displayContent={
        <div className="flex flex-col items-start">
          <h2 className="text-3xl font-bold text-gray-900">{t('invoice.title')}</h2>
          <p className="text-gray-600 mt-1">
            {t('landing.invoiceNumber', { number: displayNumber })}
          </p>
        </div>
      }
      editContent={
        <div className="space-y-6">
          {/* Preview */}
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-sm font-medium text-gray-500 mb-2">
              {t('settings.invoiceNumber.preview')}
            </p>
            <p className="text-2xl font-bold text-gray-900 tracking-tight">
              {displayNumber}
            </p>
          </div>

          {/* Prefix */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.invoiceNumber.prefix')}
            </label>
            <input
              type="text"
              value={format.prefix}
              onChange={(e) => handleFormatChange({ prefix: e.target.value.toUpperCase() })}
              placeholder={t('settings.invoiceNumber.prefixPlaceholder')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              maxLength={5}
            />
          </div>

          {/* Separator */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.invoiceNumber.separator')}
            </label>
            <div className="flex gap-2">
              {['-', '/', '.', '_', ''].map((sep) => (
                <button
                  key={sep || 'none'}
                  onClick={() => handleFormatChange({ separator: sep })}
                  className={`px-4 py-2 text-sm rounded-md flex-1 transition-colors ${
                    format.separator === sep
                      ? 'bg-primary-50 text-primary-700 font-medium ring-1 ring-primary-200'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {sep || t('settings.invoiceNumber.noSeparator')}
                </button>
              ))}
            </div>
          </div>

          {/* Year Toggle and Format */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeYear"
                  checked={format.includeYear}
                  onChange={(e) => handleFormatChange({ includeYear: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="includeYear" className="text-sm font-medium text-gray-700">
                  {t('settings.invoiceNumber.includeYear')}
                </label>
              </div>
            </div>
            {format.includeYear && (
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleFormatChange({ yearFormat: 'short' })}
                  className={`flex-1 px-4 py-2 text-sm rounded-md transition-colors ${
                    format.yearFormat === 'short'
                      ? 'bg-primary-50 text-primary-700 font-medium ring-1 ring-primary-200'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {t('settings.invoiceNumber.yearFormatShort')}
                </button>
                <button
                  onClick={() => handleFormatChange({ yearFormat: 'full' })}
                  className={`flex-1 px-4 py-2 text-sm rounded-md transition-colors ${
                    format.yearFormat === 'full'
                      ? 'bg-primary-50 text-primary-700 font-medium ring-1 ring-primary-200'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {t('settings.invoiceNumber.yearFormatFull')}
                </button>
              </div>
            )}
          </div>

          {/* Number Length */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.invoiceNumber.numberPadding')}
            </label>
            <div className="flex gap-2">
              {[4, 5, 6, 7, 8].map((num) => (
                <button
                  key={num}
                  onClick={() => handleFormatChange({ numberPadding: num })}
                  className={`flex-1 px-4 py-2 text-sm rounded-md transition-colors ${
                    format.numberPadding === num
                      ? 'bg-primary-50 text-primary-700 font-medium ring-1 ring-primary-200'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {t('settings.invoiceNumber.numberPaddingHelp')}
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <button
              onClick={handleReset}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {t('settings.invoiceNumber.resetToDefault')}
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors"
            >
              {t('actions.done')}
            </button>
          </div>
        </div>
      }
    />
  );
};

export default InvoiceNumberEdit;